import * as React from 'react';
import "bulma/css/bulma.css";
import {Button, Control, Field, Icon, Input, Label} from "bloomer";
import {changePassword, sendPasswordVerificationEmail} from "../../../utils/auth";
import Spacer from "../../../elements/Spacer";
import styled from "react-emotion";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

const InputWarning = styled("span")`
  text-align: left;
  font-size: 14px;
  color: red;
`;

const ErrorMessage = styled("span")`
  text-align: center;
  line-height: 30px;
  vertical-align: middle;
  font-size: 14px;
  color: red;
`;

interface IChangePasswordFormProps {
    onClose: any;
}

interface IChangePasswordFormState {
    sendingEmail: boolean;
    everSent: boolean;
    saving: boolean;
    code: string;
    codeInvalid: boolean;
    codeErrorMessage: string;
    password: string;
    passwordInvalid: boolean;
    passwordErrorMessage: string;
    passwordConfirmation: string;
    passwordConfirmationInvalid: boolean;
    passwordConfirmationErrorMessage: string;
    errorMessage: string;
}

class ChangePasswordForm extends React.Component<IChangePasswordFormProps, IChangePasswordFormState> {
    public CODE_LENGTH = 6;
    public CODE_REGEX = new RegExp('^[0-9]+$');
    public PASSWORD_MIN_LENGTH = 6;
    public PASSWORD_MAX_LENGTH = 30;
    public SPECIAL_CHARACTERS = '!"#$%&()*+-.:;<=>?@[]^_{|}~';

    public constructor(props: IChangePasswordFormProps) {
        super(props);

        this.state = {
            sendingEmail: false,
            everSent: false,
            saving: false,
            code: "",
            codeInvalid: false,
            codeErrorMessage: "",
            password: "",
            passwordInvalid: false,
            passwordErrorMessage: "",
            passwordConfirmation: "",
            passwordConfirmationInvalid: false,
            passwordConfirmationErrorMessage: "",
            errorMessage: ""
        };
    }

    public containsUpperCase = (str: string) => {
        return str.toLowerCase() !== str;
    };

    public containsSpecialCharacter = (str: string) => {
        return this.SPECIAL_CHARACTERS.split('').map((c: string) => {
            return str.indexOf(c) !== -1;
        }).filter(b => b).length > 0;
    };

    public handleCodeChange = (e: any) => {
        const code = e.target.value;
        const errorMsg = this.validateCode(code.trim());
        this.setState({
            code,
            codeInvalid: !!errorMsg,
            codeErrorMessage: errorMsg
        });
    };

    public handlePasswordChange = (e: any) => {
        const password = e.target.value;
        const errorMsg = this.validatePassword(password.trim());
        this.setState({
            password,
            passwordInvalid: !!errorMsg,
            passwordErrorMessage: errorMsg
        });
    };

    public handlePasswordConfirmationChange = (e: any) => {
        const passwordConfirmation = e.target.value;
        const errorMsg = this.validatePasswordConfirmation(this.state.password.trim(), passwordConfirmation);
        this.setState({
            passwordConfirmation,
            passwordConfirmationInvalid: !!errorMsg,
            passwordConfirmationErrorMessage: errorMsg
        });
    };

    public validateCode = (code: string) => {
        if (!code) {
            return "Please enter code.";
        }
        if (code.length !== this.CODE_LENGTH || !this.CODE_REGEX.test(code)) {
            return "Code should be 6 digit number.";
        }
        return "";
    };

    public validatePassword = (password: string) => {
        if (!password) {
            return "Please enter password.";
        }
        if (password.length < this.PASSWORD_MIN_LENGTH) {
            return "Password is too short.";
        }
        if (password.length > this.PASSWORD_MAX_LENGTH) {
            return "Password is too long.";
        }
        if (!this.containsUpperCase(password)) {
            return "Password must contain an uppercase character.";
        }
        if (!this.containsSpecialCharacter(password)) {
            return "Password must contain a special character.";
        }
        return "";
    };

    public validatePasswordConfirmation = (password: string, passwordConfirmation: string) => {
        if (password !== passwordConfirmation) {
            return "Passwords do not match.";
        }
        return "";
    };

    public sendCode = async () => {
        this.setState({sendingEmail: true});
        const response = await sendPasswordVerificationEmail();
        if (response) {
            this.setState({
                sendingEmail: false,
                everSent: true
            });
        }
    };

    public changePassword = async () => {
        if (this.state.sendingEmail || this.state.saving) {
            return;
        }

        const codeErrorMessage = this.validateCode(this.state.code.trim());
        const passwordErrorMessage = this.validatePassword(this.state.password.trim());
        const passwordConfirmationErrorMessage = this.validatePasswordConfirmation(
            this.state.password.trim(), this.state.passwordConfirmation.trim());

        if (codeErrorMessage || passwordErrorMessage || passwordConfirmationErrorMessage) {
            this.setState({
                codeInvalid: !!codeErrorMessage,
                codeErrorMessage,
                passwordInvalid: !!passwordErrorMessage,
                passwordErrorMessage,
                passwordConfirmationInvalid: !!passwordConfirmationErrorMessage,
                passwordConfirmationErrorMessage
            });
            return;
        }

        this.setState({
            errorMessage: "",
            saving: true
        });
        const response = await changePassword(this.state.code.trim(), this.state.password.trim());
        if (response && response.status === 204) {
            this.props.onClose(true);
        } else {
            this.setState({
                saving: false,
                errorMessage: "Invalid verification code provided. Please try again."
            });
        }
    };

    public render() {
        return <div className="modal is-active">
            <div className="modal-background" style={{
                opacity: 0.5
            }} onMouseDown={() => {
                this.props.onClose();
            }}>&nbsp;</div>
            <div className="modal-content">
                <div
                    className="has-background-light"
                    style={{
                        maxWidth: '480px',
                        margin: 'auto',
                        padding: '30px 50px',
                        textAlign: 'center',
                        position: 'relative'
                    }}
                >
                    <Button
                        title="Close"
                        className="has-background-light"
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            border: 0
                        }}
                        onClick={() => {
                            this.props.onClose();
                        }}
                    >
                        <Icon className="fas fa-times" style={{
                            color: 'grey',
                            fontSize: '24px',
                            fontWeight: 'normal'
                        }}/>
                    </Button>
                    <div style={{
                        fontSize: '24px'
                    }}>Change Password
                    </div>
                    <Spacer/>
                    <div style={{fontSize: '16px'}}>Enter the verification code and new password.
                        The verification code will be sent by email.
                    </div>
                    {
                        this.state.sendingEmail ? <div style={{lineHeight: '30px'}}>
                                <span>Sending email...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                            : <div style={{
                                lineHeight: '30px',
                                color: 'green'
                            }}>{this.state.everSent ? "Email sent." : ""}</div>
                    }
                    {
                        this.state.sendingEmail ? <></> : <React.Fragment>
                            <Button
                                className="has-background-grey-lighter"
                                style={{
                                    width: '150px'
                                }}
                                onClick={() => {
                                    this.sendCode();
                                }}
                            >{this.state.everSent ? 'Resend Email' : 'Send Email'}</Button>
                        </React.Fragment>
                    }
                    {
                        this.state.everSent ? <React.Fragment>
                            <div style={{
                                padding: '30px',
                                textAlign: 'left'
                            }}>
                                <Field>
                                    <Label>Code</Label>
                                    <Control>
                                        <Input
                                            style={this.state.codeInvalid ? INVALID_FIELD_STYLE : undefined}
                                            type="text"
                                            placeholder="Code"
                                            value={this.state.code}
                                            onChange={this.handleCodeChange}
                                        />
                                    </Control>
                                    {
                                        this.state.codeInvalid ?
                                            <InputWarning>{this.state.codeErrorMessage}</InputWarning> : <></>
                                    }
                                </Field>
                                <Field style={{textAlign: 'left'}}>
                                    <Label>New Password</Label>
                                    <Control>
                                        <Input
                                            style={this.state.passwordInvalid ? INVALID_FIELD_STYLE : undefined}
                                            type="password"
                                            autoComplete="off"
                                            placeholder="New Password"
                                            value={this.state.password}
                                            onChange={this.handlePasswordChange}
                                        />
                                    </Control>
                                    {
                                        this.state.passwordInvalid ?
                                            <InputWarning>{this.state.passwordErrorMessage}</InputWarning> : <></>
                                    }
                                </Field>
                                <Field style={{textAlign: 'left'}}>
                                    <Label>Confirm Password</Label>
                                    <Control>
                                        <Input
                                            style={this.state.passwordConfirmationInvalid ? INVALID_FIELD_STYLE : undefined}
                                            type="password"
                                            autoComplete="off"
                                            placeholder="Confirm Password"
                                            value={this.state.passwordConfirmation}
                                            onChange={this.handlePasswordConfirmationChange}
                                        />
                                    </Control>
                                    {
                                        this.state.passwordConfirmationInvalid ?
                                            <InputWarning>{this.state.passwordConfirmationErrorMessage}</InputWarning> : <></>
                                    }
                                </Field>
                                <Spacer/>
                                <Button
                                    disabled={this.state.saving || this.state.sendingEmail}
                                    className="has-background-grey-lighter"
                                    style={{width: '100%'}}
                                    onClick={(e: any) => {
                                        e.target.blur();
                                        this.changePassword();
                                    }}
                                >RESET PASSWORD</Button>
                            </div>
                        </React.Fragment> : <></>
                    }
                    {
                        this.state.saving ? <div style={{textAlign: 'center'}}>
                            <span>Loading...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div> : <></>
                    }
                    {
                        !this.state.saving && this.state.errorMessage ?
                            <ErrorMessage>{this.state.errorMessage}</ErrorMessage> : <></>
                    }
                    <Spacer/>
                </div>
            </div>
        </div>;
    }
}

export default ChangePasswordForm;


// WEBPACK FOOTER //
// ./src/routes/Settings/ChangePasswordForm/index.tsx